import type { ColumnDef } from '@tanstack/react-table';
import EmptyCell from 'components/EmptyCell';
import { Skeleton } from 'components/ui/skeleton';
import type { TFunction } from 'i18next';
import { ExternalLink, PanelRight } from 'lucide-react';
import { cn } from 'lib/utils';
import { getJiraIssueUrl } from 'utils/jiraUrls';
import { formatElapsed } from 'utils/elapsed';
import { initials } from 'utils/initials';
import type { ReferentialTag } from 'api/techniciansApi';
import {
  ASSIGNMENT_DOT,
  ASSIGNMENT_TONE,
  assignmentLabel,
  assignmentState,
  interventionDay,
  pendingSolicitationsLabel,
  refusalDetail,
  technicianName,
  type UpcomingMission,
} from './upcomingAssignment';
import { SKILL_MORE } from './helpBlock';
import { parentKeyColumn } from './columns';

/**
 * The « À qualifier » tab's columns: Clé · Parent (DPGP only) · Magasin · Type · Date de la mission ·
 * Statut Jira · Compétences · Technicien · Statut · Actions.
 *
 * **The mission date** (E4): the technician's chosen day and time once accepted, else the deadline —
 * the newest request's, as the drawer seeds it (CA15), falling back to the desk's Jira field (D15).
 *
 * No sortable header: the sort belongs to the Jira listing this tab shares, and the assignment
 * columns come from Postgres — a click would sort a page, not a result set. Every column carries
 * an `accessorFn` (what the CSV export reads) **and** an explicit `enableSorting: false` beside
 * it — the pair R-020 settled on; drop either and the other breaks silently. Row metrics come
 * from `Issues.dc.html` (14 px), overridden by the tab, not in `DataTable`'s shared `embedded`.
 */


/** « {jour} · {heure} **choisi** » once accepted with a slot, else « échéance {date} **au plus tard** »; null without a date. */
const missionDate = (t: TFunction, { request, interventionDate }: UpcomingMission): { lead: string; emphasis: string } | null => {
  const chosenDay = request?.status === 'accepted' ? interventionDay(request.chosenDate ?? null) : null;
  if (chosenDay && request?.chosenTime) {
    return { lead: t('{{day}} · {{time}}', { day: chosenDay, time: request.chosenTime }), emphasis: t('chosen') };
  }
  const deadline = interventionDay(request?.deadlineDate ?? interventionDate);
  return deadline ? { lead: t('deadline {{date}}', { date: deadline }), emphasis: t('at the latest') } : null;
};

/** Two chips then a count, the shape the directory's own specialty column uses. */
const VISIBLE = 2;

const SkillChips = ({ tags }: { tags: ReferentialTag[] }) => {
  if (tags.length === 0) return <EmptyCell />;
  return (
    <div className="flex flex-wrap items-start gap-1" title={tags.map((tag) => tag.name).join(', ')}>
      {tags.slice(0, VISIBLE).map((tag) => (
        <span
          key={tag.id}
          className="inline-block h-[22px] max-w-full truncate rounded-[6px] bg-zinc-100 px-2 text-xs font-medium leading-[22px] text-foreground dark:bg-zinc-800"
        >
          {tag.name}
        </span>
      ))}
      {tags.length > VISIBLE && (
        <span className={SKILL_MORE}>
          +{tags.length - VISIBLE}
        </span>
      )}
    </div>
  );
};

/**
 * A real button, bordered, 30 px — the mock-up's `primBtn` / `ghostBtn`. It used to be bare primary
 * text, which read as one more link in a row that already has one.
 */
const ACTION =
  'inline-flex h-[30px] items-center gap-1.5 rounded-lg border px-3 text-[12.5px] font-medium transition-colors disabled:opacity-50';
const ACTION_PRIMARY = 'border-primary bg-primary text-primary-foreground hover:bg-primary/90';
const ACTION_GHOST = 'border-input bg-background text-foreground hover:bg-accent';

interface UpcomingColumnsOptions {
  t: TFunction;
  canManage: boolean;
  /** Opens the assignment drawer, which is where every action on a mission now lives. */
  onOpen: (mission: UpcomingMission) => void;
  jiraBrowseBase?: string | null;
  /** The mission whose panel is open — the row marks it with the mock-up's 3 px accent. */
  selectedKey?: string | null;
  /**
   * Whether the Postgres assignment read landed. False while it is in flight and after it failed:
   * `mergeAssignments` then defaults every mission to `request: null`, which reads as « Sans
   * technicien » — a claim the screen cannot support. The two assignment columns say nothing then.
   */
  assignmentsKnown?: boolean;
  /** The store names arrive after the listing; until then the cell greys rather than empties. */
  storeLabelsLoading?: boolean;
  onParentFilter?: (key: string) => void;
}

export const getUpcomingColumns = ({
  t,
  canManage,
  onOpen,
  jiraBrowseBase,
  selectedKey = null,
  assignmentsKnown = true,
  storeLabelsLoading = false,
  onParentFilter,
}: UpcomingColumnsOptions): ColumnDef<UpcomingMission>[] => {
  const columns: ColumnDef<UpcomingMission>[] = [
    {
      id: 'issueKey',
      header: t('Key'),
      accessorFn: (mission) => mission.issueKey,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-[7px]">
          {/* Rendered on every row, coloured on one: an accent that appears and disappears would
              shift the key by 3 px each time a panel opens. */}
          <span
            className={cn(
              'h-[18px] w-[3px] shrink-0 rounded-sm',
              row.original.issueKey === selectedKey ? 'bg-primary' : 'bg-transparent'
            )}
            aria-hidden="true"
          />
          <a
            href={getJiraIssueUrl(row.original.issueKey, jiraBrowseBase)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-mono text-[13px] font-semibold text-foreground hover:text-primary hover:underline"
          >
            {row.original.issueKey}
            <ExternalLink className="h-[13px] w-[13px] text-muted-foreground/70" aria-hidden="true" />
          </a>
        </span>
      ),
    },
    ...(onParentFilter ? [parentKeyColumn<UpcomingMission>(t, onParentFilter, jiraBrowseBase)] : []),
    {
      id: 'store',
      header: t('Store'),
      accessorFn: (mission) => mission.store ?? '',
      enableSorting: false,
      cell: ({ row }) => {
        const { store, storeObjectId } = row.original;
        if (store) {
          return (
            <span className="block max-w-[220px] truncate text-[13.5px] font-medium" title={store}>
              {store}
            </span>
          );
        }
        // Greyed, not « — »: the name is on its way, the ticket does have a store.
        return storeObjectId && storeLabelsLoading ? <Skeleton className="h-4 w-32" /> : <EmptyCell />;
      },
    },
    {
      /*
        The tab decides which tickets belong to it from their Jira type and status, and until
        those filters are frozen and hidden (L4d) the only way to trust the list is to read both
        on the row. Headed « Type » like the other tab, because it is the same field.

        This replaces the « Mission » column, which rendered `summary` and carried nothing extra:
        measured live, DPGP has 65/100 rows where the two strings are equal and the rest differ by
        a word (« Déplacement mobilier » / « Déplacement »), while on LUXE the request type is
        strictly richer — « MUP - Demande Intervention Technicien mobilier » against a summary
        reading « Intervention Technique ». The mock-up agrees: its own « Mission » cell renders
        `m.type`. `requestType` can be null on a ticket ingested before the id was captured — an
        em dash, never an invented type.
      */
      id: 'requestType',
      header: t('Type'),
      accessorFn: (mission) => mission.requestType ?? '',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.requestType ? (
          <span className="block max-w-[230px] truncate text-[13px]" title={row.original.requestType}>
            {row.original.requestType}
          </span>
        ) : (
          <EmptyCell />
        ),
    },
    {
      // No sort (ruling): the Jira cursor listing this tab shares cannot order on a Postgres date.
      id: 'interventionDate',
      header: t('Mission date'),
      accessorFn: (mission) => {
        const date = missionDate(t, mission);
        return date ? `${date.lead} ${date.emphasis}` : '';
      },
      enableSorting: false,
      cell: ({ row }) => {
        const date = missionDate(t, row.original);
        return date ? (
          <span className="text-[13px] text-foreground">
            {date.lead} <strong className="font-semibold">{date.emphasis}</strong>
          </span>
        ) : (
          <EmptyCell />
        );
      },
    },
    {
      id: 'jiraStatus',
      header: t('Jira status'),
      accessorFn: (mission) => mission.status,
      enableSorting: false,
      cell: ({ row }) => (
        <span
          className="block max-w-[150px] truncate text-[12.5px] text-muted-foreground"
          title={`${row.original.status} (${row.original.statusId})`}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      id: 'specialties',
      header: t('Skills'),
      accessorFn: (mission) => mission.specialties.map((specialty) => specialty.name).join(' · '),
      enableSorting: false,
      cell: ({ row }) => <SkillChips tags={row.original.specialties} />,
    },
    {
      id: 'technician',
      header: t('Technician'),
      // The export says what the cell says — a name exported under « 2 sollicités » would put one
      // technician on a mission the screen refuses to attribute.
      accessorFn: (mission) => {
        if (!assignmentsKnown) return '';
        const awaiting = pendingSolicitationsLabel(t, mission.request?.status, mission.liveRequestCount);
        if (awaiting) return awaiting;
        return mission.request ? technicianName(mission.request) : '';
      },
      enableSorting: false,
      cell: ({ row }) => {
        const { request } = row.original;
        // An unread assignment is not an empty one: the dash is the one marker that claims nothing.
        if (!assignmentsKnown) return <EmptyCell />;
        // Several asked and nobody retained: a figure, no avatar and no name (L5b).
        const awaiting = pendingSolicitationsLabel(t, row.original.request?.status, row.original.liveRequestCount);
        if (awaiting) return <span className="text-[13px] text-muted-foreground">{awaiting}</span>;
        // Not `<Empty/>`: a dash says "no value", the mock-up says what has to happen next.
        if (!request)
          return <span className="text-[13px] text-muted-foreground/70">{t('To assign')}</span>;
        return (
          <div className="flex min-w-0 items-center gap-[9px]">
            <span
              className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px] bg-zinc-100 text-[10.5px] font-semibold text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
              aria-hidden="true"
            >
              {initials(technicianName(request))}
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
            <span className="block max-w-[180px] truncate text-[13px] font-medium" title={request.technician.username}>
              {technicianName(request)}
            </span>
            {/*
             * The time elapsed since the send, on the row — scope, not comfort. Sequential
             * sending plus no expiry (D5) means a mission left with someone who never answers
             * stays stuck with no alert; this is what makes the wait visible without opening the
             * drawer. Only while it is a wait: once answered, the delay is the follow-up's
             * subject (L5), not this row's.
             */}
            {request.status === 'pending' && (
              <span className="block text-[11px] text-muted-foreground/70">
                {formatElapsed(request.elapsedSeconds)}
              </span>
            )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'status',
      // The export gets the same sentence the badge shows, refusal reason included — never a raw
      // `pending` nor a raw `too_far`.
      accessorFn: (mission) => {
        if (!assignmentsKnown) return '';
        const label = assignmentLabel(t, assignmentState(mission.request));
        const detail = refusalDetail(t, mission.request);
        return detail ? `${label} — ${detail}` : label;
      },
      header: t('Status'),
      enableSorting: false,
      cell: ({ row }) => {
        // No badge at all rather than a zinc « À traiter »: a tone is itself an assertion.
        if (!assignmentsKnown) return <EmptyCell />;
        const state = assignmentState(row.original.request);
        const detail = refusalDetail(t, row.original.request);
        return (
          <span
            className={cn(
              'inline-flex h-[23px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full pl-2 pr-[9px] text-[11.5px] font-medium',
              ASSIGNMENT_TONE[state]
            )}
            // The refusal reason is what the GPS team reads to decide who to try next. On the row
            // it is a hover, on the drawer's card it is written out. Already null unless refused.
            title={detail ?? undefined}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', ASSIGNMENT_DOT[state])} aria-hidden="true" />
            {assignmentLabel(t, state)}
          </span>
        );
      },
    },
  ];

  // No Actions column at all for a view-only admin, rather than buttons that answer 403:
  // `technicians:view` opens this tab, `technicians:manage` is what sends.
  if (!canManage) return columns;

  return [
    ...columns,
    {
      id: 'actions',
      header: t('Actions'),
      enableSorting: false,
      meta: { cellClassName: 'text-right' },
      cell: ({ row }) => {
        const mission = row.original;
        const state = assignmentState(mission.request);
        // Filled when the row is waiting on a decision, ghost when it already has an answer —
        // the mock-up's `needs ? primBtn : ghostBtn`, so a page of rows shows where to look.
        const needsSomeone = assignmentsKnown && (state === 'unassigned' || state === 'declined');
        return (
          <div className="flex items-center justify-end">
            {/* One action per row: two buttons crowded the row, and the send is a decision that
                needs the drawer's context anyway — removal included. Disabled, never removed, while
                the assignment is unknown: the drawer would open on the same `request: null` the
                columns refuse to render, and a column that comes and goes is worse than a dead
                button. The card's own `role="alert"` carries the reason at full size. */}
            <button
              type="button"
              disabled={!assignmentsKnown}
              title={assignmentsKnown ? undefined : t('The assignments are not loaded — this mission cannot be acted on yet.')}
              onClick={() => onOpen(mission)}
              className={cn(ACTION, needsSomeone ? ACTION_PRIMARY : ACTION_GHOST)}
            >
              <PanelRight className="h-4 w-4" aria-hidden="true" />
              {t('Details')}
            </button>
          </div>
        );
      },
    },
  ];
};
